import { useState, useRef, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { getApiBaseUrlForBrowserFetch, PREVIEW_PR } from '../lib/previewApiLookup';
import { isAudioUnlocked, playThroughAudioContext } from '../lib/mobileAudioUnlock';

type TtsProvider = 'browser' | 'openai';

interface AudioPlaybackHook {
  isPlaying: boolean;
  play: (text: string, sessionId?: string, personaId?: string) => Promise<void>;
  stop: () => void;
}

export function useAudioPlayback(provider: TtsProvider = 'browser'): AudioPlaybackHook {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const webAudioStopRef = useRef<(() => void) | null>(null);
  const providerRef = useRef(provider);
  providerRef.current = provider;

  const revokeUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (webAudioStopRef.current) {
      webAudioStopRef.current();
      webAudioStopRef.current = null;
    }
    revokeUrl();
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    utteranceRef.current = null;
    setIsPlaying(false);
  }, [revokeUrl]);

  const playBrowserTts = useCallback(
    (text: string): Promise<void> =>
      new Promise((resolve) => {
        if (!window.speechSynthesis) {
          resolve();
          return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utteranceRef.current = utterance;

        utterance.onend = () => {
          setIsPlaying(false);
          utteranceRef.current = null;
          resolve();
        };
        utterance.onerror = () => {
          setIsPlaying(false);
          utteranceRef.current = null;
          resolve();
        };

        setIsPlaying(true);
        window.speechSynthesis.speak(utterance);
      }),
    [],
  );

  const play = useCallback(
    async (text: string, sessionId?: string, personaId?: string) => {
      stop();
      const trimmed = text?.trim() ?? '';
      if (!trimmed) {
        return;
      }

      if (providerRef.current === 'browser') {
        await playBrowserTts(trimmed);
        return;
      }

      const playViaWebAudioApi = async (arrayBuf: ArrayBuffer): Promise<void> => {
        try {
          const handle = await playThroughAudioContext(
            arrayBuf,
            () => setIsPlaying(true),
            () => {
              setIsPlaying(false);
              webAudioStopRef.current = null;
            },
          );
          webAudioStopRef.current = handle.stop;
        } catch {
          await playBrowserTts(trimmed);
        }
      };

      // Do not set isPlaying until audio is about to play. Setting it earlier pauses Web Speech
      // (echo guard in VerbalSessionView) for the whole TTS fetch, which blocks fluid capture.
      await Sentry.startSpan({ name: 'practice.tts_playback', op: 'ui.action' }, async () => {
        try {
          const baseUrl = await getApiBaseUrlForBrowserFetch();
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          const token = sessionStorage.getItem('token');
          if (token) headers['Authorization'] = `Bearer ${token}`;
          if (PREVIEW_PR) headers['X-Preview-PR'] = PREVIEW_PR;

          const fetchRes = await fetch(`${baseUrl}/practice/tts`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              text: trimmed,
              ...(sessionId ? { sessionId } : {}),
              ...(personaId ? { personaId } : {}),
            }),
          });

          if (!fetchRes.ok || !fetchRes.body) {
            await playBrowserTts(trimmed);
            return;
          }

          const contentType = fetchRes.headers.get('content-type') || '';
          if (!contentType.includes('audio')) {
            await playBrowserTts(trimmed);
            return;
          }

          // MediaSource allows us to feed chunks to the audio element as they
          // arrive, giving true streaming playback. Fall back to blob if the
          // browser doesn't support the mpeg codec in MediaSource.
          const canStream =
            typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');

          /**
           * Try HTMLAudioElement playback first; if the browser rejects it
           * (mobile autoplay policy), fall back to the Web Audio API which
           * works as long as the shared AudioContext was unlocked by a prior
           * user gesture (see mobileAudioUnlock.ts).
           */
          const playViaHtmlAudio = async (audio: HTMLAudioElement): Promise<boolean> => {
            try {
              await audio.play();
              return true;
            } catch (playErr: unknown) {
              const name = playErr instanceof DOMException ? playErr.name : '';
              if (name !== 'NotAllowedError') throw playErr;
              return false;
            }
          };

          if (canStream) {
            const mediaSource = new MediaSource();
            const url = URL.createObjectURL(mediaSource);
            objectUrlRef.current = url;
            const audio = new Audio(url);
            audioRef.current = audio;

            mediaSource.addEventListener('sourceopen', async () => {
              const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
              const reader = fetchRes.body!.getReader();

              const pump = async (): Promise<void> => {
                const { done, value } = await reader.read();
                if (done) {
                  if (mediaSource.readyState === 'open') {
                    if (sourceBuffer.updating) {
                      await new Promise<void>((r) =>
                        sourceBuffer.addEventListener('updateend', () => r(), { once: true }),
                      );
                    }
                    mediaSource.endOfStream();
                  }
                  return;
                }
                sourceBuffer.appendBuffer(value);
                await new Promise<void>((r) =>
                  sourceBuffer.addEventListener('updateend', () => r(), { once: true }),
                );
                return pump();
              };

              pump().catch(() => {
                if (mediaSource.readyState === 'open') {
                  try {
                    mediaSource.endOfStream();
                  } catch {
                    /* ignore */
                  }
                }
              });
            });

            audio.onended = () => {
              setIsPlaying(false);
              revokeUrl();
              audioRef.current = null;
            };
            audio.onerror = () => {
              setIsPlaying(false);
              revokeUrl();
              audioRef.current = null;
            };

            setIsPlaying(true);
            const played = await playViaHtmlAudio(audio);
            if (!played) {
              // MediaSource streaming path blocked — collect remaining data
              // and play through Web Audio API.
              revokeUrl();
              audioRef.current = null;
              const arrayBuf = await new Response(fetchRes.body).arrayBuffer();
              await playViaWebAudioApi(arrayBuf);
            }
          } else {
            const blob = await fetchRes.blob();

            // Prefer Web Audio API on mobile if AudioContext is already unlocked,
            // because HTMLAudioElement.play() may be blocked by autoplay policy.
            if (isAudioUnlocked()) {
              const arrayBuf = await new Response(blob).arrayBuffer();
              await playViaWebAudioApi(arrayBuf);
            } else {
              const url = URL.createObjectURL(blob);
              objectUrlRef.current = url;
              const audio = new Audio(url);
              audioRef.current = audio;

              audio.onended = () => {
                setIsPlaying(false);
                revokeUrl();
                audioRef.current = null;
              };
              audio.onerror = () => {
                setIsPlaying(false);
                revokeUrl();
                audioRef.current = null;
              };

              setIsPlaying(true);
              const played = await playViaHtmlAudio(audio);
              if (!played) {
                revokeUrl();
                audioRef.current = null;
                const arrayBuf = await new Response(blob).arrayBuffer();
                await playViaWebAudioApi(arrayBuf);
              }
            }
          }
        } catch (err) {
          Sentry.captureException(err, { tags: { subsystem: 'openai_tts' } });
          await playBrowserTts(trimmed);
        }
      });
    },
    [stop, playBrowserTts, revokeUrl],
  );

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return { isPlaying, play, stop };
}
